import {
  appleMask,
  crabMask,
  crownMask,
  fishMask,
  moonMask,
  mushroomMask,
  rectMask,
  rocketMask,
  starMask,
  type BoardMask,
} from './mask';
import { createRng, deriveSeed, fnv1a32Hex, shuffle } from './rng';
import type {
  ChainTrapParams,
  EdgeParams,
  GenerationInput,
  GenerationParams,
  VacancyParams,
  WardenParams,
} from './types';

export interface LevelPlan {
  readonly levelNumber: number;
  readonly maskId: string;
  readonly paramsId: string;
  readonly baseSeed: number;
}

interface BandDefinition {
  readonly maxLevel: number;
  readonly scale: number;
}

export type EraId = 'A' | 'B' | 'C' | 'D';
export type LevelIntensity = 'normal' | 'hard' | 'superhard';

export const largeBoardGenerationPasses = 32;
export const largeRectanglePartitionAlgorithmVersion = 5;
export const largeRectangleHamiltonianPartitionThreshold = 24 * 26;
export const largeRectangleShortPathMaximumLength = 3;
export const largeRectangleShortPathRatioMinimum = 0.05;
export const largeRectangleShortPathRatioMaximum = 0.2;
export const largeRectangleShortPathMergeThreshold = 0.2;
export const initialRemovableDispersionMinimum = 0.25;
export const wardenOrientationAlgorithmVersion = 2;
export const wardenAuthoringRateRanges = {
  hard: [0.05, 0.1],
  superhard: [0.03, 0.08],
} as const;
export const wardenSuperhardFallbackRateMaximum = 0.14;
// hard の strict 上限 0.10 は一部レベルで実現不能（level 608 が 128 リトライ全滅）。
// 旧上限 0.12 を relaxed として残し、生成可能性を保証する
export const wardenHardFallbackRateMaximum = 0.12;
export const wardenStrictTargetAttemptCount = 24;
export const wardenHardLongBlockerMinimum = 0.4;
export const wardenSuperhardLongBlockerMinimum = 0.55;
export const wardenSuperhardRelaxedLongBlockerMinimum = 0.45;
export const wardenSuperhardPeelEnforcementCellThreshold = 30 * 42;
export const edgeSentinelAxisArrowCountMinimum = 6;
export const edgeSentinelStrictInwardCountPerSideMinimum = 1;
export const edgeSentinelRelaxedInwardCountMinimum = 1;
export const edgeSentinelStrictAttemptCount = 24;
// 空白彫り出しの削除候補採点。freed（削除で除去可能化する矢印）は初期除去可能率を
// 押し上げるため、遠距離ブロック化 1 件の利得よりも強く罰する
export const vacancyReblockWeight = 10;
export const vacancyFreedPenalty = 30;
export const vacancyLengthPenalty = 1;
export const partitionRestartLimit = 16;
export const snakeAttemptLimit = 24;
// Keep this in the definitions hash so a threshold change without regeneration
// is detected at startup instead of failing later while reproducing a resolved seed.
export const generationEffortLimit = 10000;
// 256 への引き上げは ADR-0009 の B（選択肢圧縮）に伴う。素の試行の大半が高 rate の層
// （例: rect-28x38/hard-mixed）はガードが稀な当たりを拾う構造で、strict 窓の追加により
// 128 では枯渇するレベルが実発生した（level 1203, 2253）
export const authoredGenerationRetryLimit = 256;
export const smallPeakOrientationAlgorithmVersion = 2;
export const chainTrapOrientationAlgorithmVersion = 5;
export const vacancyCarvingAlgorithmVersion = 1;
export const chainPatchPartitionAlgorithmVersion = 1;
export const chainPatchArrowCount = 4;
export const chainPatchArrowLength = 4;
export const chainPatchCornerMargin = 2;
export const chainTrapNormalEnforcementCellThreshold = 20 * 20 + 1;
export const chainTrapNormalStrictAttemptCount = 16;
export const chainTrapHardStrictAttemptCount = 16;
export const chainTrapHardFallbackTrapMinimum = 1;
export const nearSpanningBandGuardAlgorithmVersion = 1;
export const nearSpanningBandMaximumEdgeGap = 2;
export const nearSpanningBandMinimumConsecutiveLines = 3;

const paramsTypeSeedSalt = 3;

export const silhouetteIntroductionMaximumLevel = 100;
export const silhouetteTargetRate = 0.1;
export const silhouetteMaskAssignmentAlgorithmVersion = 1;
const silhouetteRhythmSeedSalt = Number.parseInt(
  fnv1a32Hex('board-shape-rhythm/eligibility-v1'),
  16,
);
const silhouetteMaskShuffleSeedSalts = {
  normal: Number.parseInt(fnv1a32Hex('board-shape-rhythm/normal-mask-shuffle-v1'), 16),
  hard: Number.parseInt(fnv1a32Hex('board-shape-rhythm/hard-mask-shuffle-v1'), 16),
} as const;

const rhythmRunLengthWeights = [
  { length: 2, weight: 0.2 },
  { length: 3, weight: 0.45 },
  { length: 4, weight: 0.35 },
] as const;
const rhythmSuperhardRatio = 0.25;
const rhythmMinimumPeaksBetweenSuperhard = 2;
const rhythmSeedSalts = {
  runLength: Number.parseInt(fnv1a32Hex('difficulty-rhythm/run-length-v1'), 16),
  peakIntensity: Number.parseInt(fnv1a32Hex('difficulty-rhythm/peak-intensity-v1'), 16),
} as const;

const baseParamsTypes = [
  { id: 'dense', params: { lengthMix: [[2, 3, 0.06], [4, 6, 0.69], [16, 24, 0.25]], turnBias: 0.55, orientationPasses: 1 } },
  { id: 'mixed', params: { lengthMix: [[2, 3, 0.05], [4, 7, 0.62], [18, 28, 0.33]], turnBias: 0.65, orientationPasses: 1 } },
  { id: 'long', params: { lengthMix: [[2, 3, 0.04], [5, 8, 0.54], [20, 32, 0.42]], turnBias: 0.7, orientationPasses: 1 } },
  { id: 'straight', params: { lengthMix: [[2, 3, 0.05], [4, 7, 0.67], [20, 32, 0.28]], turnBias: 0.25, orientationPasses: 1 } },
  { id: 'maze', params: { lengthMix: [[2, 3, 0.04], [5, 8, 0.64], [18, 28, 0.32]], turnBias: 0.85, orientationPasses: 1 } },
  { id: 'titan', params: { lengthMix: [[2, 3, 0.06], [4, 6, 0.59], [24, 40, 0.35]], turnBias: 0.7, orientationPasses: 1 } },
] as const satisfies readonly { readonly id: string; readonly params: GenerationParams }[];

export const paramsTypeIds: readonly string[] = baseParamsTypes.map(({ id }) => id);

const wardenParamsByIntensity = {
  normal: undefined,
  hard: {
    count: [3, 5],
    minimumLength: 12,
    orientationCandidates: 2,
    initialRemovableRateTarget: 0.07,
    peelDepthTarget: 18,
    longBlockerRatioTarget: 0.4,
    mergeLengthMultiplier: 3,
    victimChunkLength: 12,
    maximumVictimChunks: 3,
    victimSplitPreference: 0.8,
    candidatePoolMultiplier: 4,
    selectionPreference: 1,
    victimOrientationPreference: 1,
    chainOrientationPreference: 1,
  },
  superhard: {
    count: [8, 8],
    minimumLength: 12,
    orientationCandidates: 2,
    initialRemovableRateTarget: 0.05,
    peelDepthTarget: 25,
    longBlockerRatioTarget: 0.6,
    mergeLengthMultiplier: 4,
    victimChunkLength: 12,
    maximumVictimChunks: 4,
    victimSplitPreference: 1,
    candidatePoolMultiplier: 4,
    selectionPreference: 1,
    victimOrientationPreference: 1,
    chainOrientationPreference: 1,
  },
} as const satisfies Readonly<Record<LevelIntensity, WardenParams | undefined>>;

const chainTrapParamsByIntensity = {
  normal: {
    orientationCandidates: 2,
    chainRunTarget: 1,
    trapCountTarget: [0, 1],
    maximumTrapCount: 1,
    initialRemovableRate: [0.1, 0.25],
  },
  hard: {
    orientationCandidates: 8,
    chainRunTarget: 1,
    trapCountTarget: [2, 6],
    maximumTrapCount: 7,
    initialRemovableRate: [0.04, 0.1],
  },
  superhard: undefined,
} as const satisfies Readonly<Record<LevelIntensity, ChainTrapParams | undefined>>;

const edgeParamsByIntensity = {
  normal: { sentinelCount: [1, 1], exitBandDivisor: 4 },
  hard: { sentinelCount: [4, 5], exitBandDivisor: 4 },
  superhard: { sentinelCount: [5, 7], exitBandDivisor: 4 },
} as const satisfies Readonly<Record<LevelIntensity, EdgeParams>>;

const vacancyParamsByIntensity = {
  normal: undefined,
  hard: { deletionCount: [3, 5], longRangeDistance: 4 },
  superhard: { deletionCount: [4, 6], longRangeDistance: 4 },
} as const satisfies Readonly<Record<LevelIntensity, VacancyParams | undefined>>;

const paramsTypes = (['normal', 'hard', 'superhard'] as const).flatMap((intensity) =>
  baseParamsTypes.map(({ id, params }) => ({
    id: intensity === 'normal' ? id : `${intensity}-${id}`,
    params: {
      ...params,
      orientationPasses: intensity === 'normal' ? 1 : 16,
      warden: wardenParamsByIntensity[intensity],
      chainTrap: chainTrapParamsByIntensity[intensity],
      edge: edgeParamsByIntensity[intensity],
      vacancy: vacancyParamsByIntensity[intensity],
    },
  })));

const paramsIdsByIntensity = {
  normal: paramsTypeIds,
  hard: paramsTypeIds.map((id) => `hard-${id}`),
  superhard: paramsTypeIds.map((id) => `superhard-${id}`),
} as const satisfies Readonly<Record<LevelIntensity, readonly string[]>>;

const paramsDefinitions = new Map<string, GenerationParams>(
  paramsTypes.map(({ id, params }) => [id, params]),
);

const bands: readonly BandDefinition[] = [
  { maxLevel: 10, scale: 0.25 },
  { maxLevel: 30, scale: 0.33 },
  { maxLevel: 50, scale: 0.4 },
  { maxLevel: 70, scale: 0.48 },
  { maxLevel: 100, scale: 0.55 },
  { maxLevel: 500, scale: 0.82 },
  { maxLevel: 1500, scale: 1.1 },
  { maxLevel: 3000, scale: 1.5 },
  { maxLevel: Number.POSITIVE_INFINITY, scale: 2 },
];

const rectangleSizeList = [
  { id: 'rect-6x8', width: 6, height: 8, cellCount: 48 },
  { id: 'rect-8x10', width: 8, height: 10, cellCount: 80 },
  { id: 'rect-10x12', width: 10, height: 12, cellCount: 120 },
  { id: 'rect-12x14', width: 12, height: 14, cellCount: 168 },
  { id: 'rect-14x16', width: 14, height: 16, cellCount: 224 },
  { id: 'rect-16x16', width: 16, height: 16, cellCount: 256 },
  { id: 'rect-16x18', width: 16, height: 18, cellCount: 288 },
  { id: 'rect-18x18', width: 18, height: 18, cellCount: 324 },
  { id: 'rect-18x20', width: 18, height: 20, cellCount: 360 },
  { id: 'rect-20x20', width: 20, height: 20, cellCount: 400 },
  { id: 'rect-20x22', width: 20, height: 22, cellCount: 440 },
  { id: 'rect-22x22', width: 22, height: 22, cellCount: 484 },
  { id: 'rect-22x24', width: 22, height: 24, cellCount: 528 },
  { id: 'rect-24x24', width: 24, height: 24, cellCount: 576 },
  { id: 'rect-24x26', width: 24, height: 26, cellCount: 624 },
  { id: 'rect-24x30', width: 24, height: 30, cellCount: 720 },
  { id: 'rect-26x34', width: 26, height: 34, cellCount: 884 },
  { id: 'rect-28x38', width: 28, height: 38, cellCount: 1064 },
  { id: 'rect-30x42', width: 30, height: 42, cellCount: 1260 },
  { id: 'rect-32x46', width: 32, height: 46, cellCount: 1472 },
  { id: 'rect-34x48', width: 34, height: 48, cellCount: 1632 },
  { id: 'rect-36x50', width: 36, height: 50, cellCount: 1800 },
] as const;
export const rectangleMaskIds: readonly string[] = rectangleSizeList.map(({ id }) => id);
const rectangleMasks = new Map<string, BoardMask>(
  rectangleSizeList.map(({ id, width, height }) => [id, rectMask(id, width, height)]),
);

const silhouetteMasksByIntensity = {
  normal: [fishMask, moonMask, mushroomMask, appleMask],
  hard: [starMask, crownMask, rocketMask, crabMask],
} as const;
export const silhouetteMaskIdsByIntensity = {
  normal: silhouetteMasksByIntensity.normal.map(({ id }) => id),
  hard: silhouetteMasksByIntensity.hard.map(({ id }) => id),
} as const;
export const silhouetteMaskIds: readonly string[] = [
  ...silhouetteMaskIdsByIntensity.normal,
  ...silhouetteMaskIdsByIntensity.hard,
];
const silhouetteMasks = new Map<string, BoardMask>([
  ...silhouetteMasksByIntensity.normal,
  ...silhouetteMasksByIntensity.hard,
].map((mask) => [mask.id, mask]));
const silhouetteMaskOrderByIntensity = {
  normal: shuffle(
    createRng(silhouetteMaskShuffleSeedSalts.normal),
    silhouetteMaskIdsByIntensity.normal,
  ),
  hard: shuffle(
    createRng(silhouetteMaskShuffleSeedSalts.hard),
    silhouetteMaskIdsByIntensity.hard,
  ),
} as const;

const intensityCellTargets = {
  normal: 300,
  hard: 700,
  superhard: 900,
} as const satisfies Readonly<Record<LevelIntensity, number>>;

const intensityCache: LevelIntensity[] = [];
const silhouetteMaskCache: (string | undefined)[] = [];
let nextRhythmBlockNumber = 0;
let nextPeakNumber = 0;
let lastSuperhardPeakNumber: number | undefined;
const silhouetteMaskUseCountByIntensity = { normal: 0, hard: 0 };

function assertValidLevelNumber(levelNumber: number): void {
  if (!Number.isInteger(levelNumber) || levelNumber < 1) {
    throw new Error(`levelNumber must be a positive integer: ${levelNumber}`);
  }
}

function nearestRectangle(targetCellCount: number): typeof rectangleSizeList[number] {
  return rectangleSizeList.reduce((nearest, candidate) => {
    const nearestDistance = Math.abs(nearest.cellCount - targetCellCount);
    const candidateDistance = Math.abs(candidate.cellCount - targetCellCount);
    return candidateDistance < nearestDistance ? candidate : nearest;
  });
}

export function bandMaxLevelForLevel(levelNumber: number): number {
  assertValidLevelNumber(levelNumber);
  const band = bands.find((candidate) => levelNumber <= candidate.maxLevel);
  if (band === undefined) {
    throw new Error(`No difficulty band for level ${levelNumber}.`);
  }
  return band.maxLevel;
}

export function intensityForLevel(levelNumber: number): LevelIntensity {
  assertValidLevelNumber(levelNumber);
  while (intensityCache.length < levelNumber) {
    const blockNumber = nextRhythmBlockNumber;
    nextRhythmBlockNumber += 1;

    const runSample = deriveSeed(blockNumber, rhythmSeedSalts.runLength) / 0x1_0000_0000;
    let cumulativeWeight = 0;
    let runLength: 2 | 3 | 4 = 4;
    for (const candidate of rhythmRunLengthWeights) {
      cumulativeWeight += candidate.weight;
      if (runSample < cumulativeWeight) {
        runLength = candidate.length;
        break;
      }
    }
    intensityCache.push(...Array.from<LevelIntensity>({ length: runLength }).fill('normal'));

    const peakNumber = nextPeakNumber;
    nextPeakNumber += 1;
    const separatedFromLastSuperhard = lastSuperhardPeakNumber === undefined
      || peakNumber - lastSuperhardPeakNumber > rhythmMinimumPeaksBetweenSuperhard;
    const peakSample = deriveSeed(blockNumber, rhythmSeedSalts.peakIntensity)
      / 0x1_0000_0000;
    const peak: LevelIntensity = separatedFromLastSuperhard
      && peakSample < rhythmSuperhardRatio
      ? 'superhard'
      : 'hard';
    if (peak === 'superhard') {
      lastSuperhardPeakNumber = peakNumber;
    }
    intensityCache.push(peak);
  }
  const intensity = intensityCache[levelNumber - 1];
  if (intensity === undefined) {
    throw new Error(`No intensity for level ${levelNumber}.`);
  }
  return intensity;
}

function paramsIdForLevel(levelNumber: number, intensity: LevelIntensity): string {
  const candidates = paramsIdsByIntensity[intensity];
  const paramsId = candidates[deriveSeed(levelNumber, paramsTypeSeedSalt) % candidates.length];
  if (paramsId === undefined) {
    throw new Error(`No params type for level ${levelNumber}.`);
  }
  return paramsId;
}

export function rectangleMaskIdForLevel(
  levelNumber: number,
  intensity = intensityForLevel(levelNumber),
): string {
  assertValidLevelNumber(levelNumber);
  const band = bands.find((candidate) => levelNumber <= candidate.maxLevel);
  if (band === undefined) {
    throw new Error(`No difficulty band for level ${levelNumber}.`);
  }
  const targetCellCount = Math.round(intensityCellTargets[intensity] * band.scale);
  return nearestRectangle(targetCellCount).id;
}

function dispersedSilhouetteMaskId(levelNumber: number, intensity: 'normal' | 'hard'): string {
  const candidates = silhouetteMaskOrderByIntensity[intensity];
  const useCount = silhouetteMaskUseCountByIntensity[intensity];
  const maskId = candidates[useCount % candidates.length];
  if (maskId === undefined) {
    throw new Error(`No silhouette mask for level ${levelNumber}.`);
  }
  silhouetteMaskUseCountByIntensity[intensity] += 1;
  return maskId;
}

export function silhouetteMaskIdForLevel(levelNumber: number): string | undefined {
  assertValidLevelNumber(levelNumber);
  while (silhouetteMaskCache.length < levelNumber) {
    const candidateLevel = silhouetteMaskCache.length + 1;
    const intensity = intensityForLevel(candidateLevel);
    const previousWasSilhouette = silhouetteMaskCache.at(-1) !== undefined;
    const sample = deriveSeed(candidateLevel, silhouetteRhythmSeedSalt) / 0x1_0000_0000;
    const selected = candidateLevel > silhouetteIntroductionMaximumLevel
      && intensity !== 'superhard'
      && !previousWasSilhouette
      && sample < silhouetteTargetRate;
    silhouetteMaskCache.push(selected
      ? dispersedSilhouetteMaskId(candidateLevel, intensity)
      : undefined);
  }
  return silhouetteMaskCache[levelNumber - 1];
}

export function planForLevel(levelNumber: number): LevelPlan {
  assertValidLevelNumber(levelNumber);
  const intensity = intensityForLevel(levelNumber);
  return {
    levelNumber,
    maskId: silhouetteMaskIdForLevel(levelNumber)
      ?? rectangleMaskIdForLevel(levelNumber, intensity),
    paramsId: paramsIdForLevel(levelNumber, intensity),
    baseSeed: levelNumber,
  };
}

export function eraIdForLevel(levelNumber: number): EraId | undefined {
  assertValidLevelNumber(levelNumber);
  if (levelNumber <= 100) {
    return undefined;
  }
  if (levelNumber <= 500) return 'A';
  if (levelNumber <= 1500) return 'B';
  if (levelNumber <= 3000) return 'C';
  return 'D';
}

export function maskById(maskId: string): BoardMask {
  const rectangle = rectangleMasks.get(maskId);
  if (rectangle !== undefined) {
    return rectangle;
  }
  const silhouette = silhouetteMasks.get(maskId);
  if (silhouette !== undefined) {
    return silhouette;
  }
  throw new Error(`Unknown mask ID: ${maskId}`);
}

export function paramsById(paramsId: string): GenerationParams {
  const params = paramsDefinitions.get(paramsId);
  if (params === undefined) {
    throw new Error(`Unknown params ID: ${paramsId}`);
  }
  return params;
}

export function inputForLevel(
  levelNumber: number,
  seed: number,
  maskId: string,
  paramsId: string,
): GenerationInput {
  return {
    seed,
    mask: maskById(maskId),
    params: paramsById(paramsId),
    levelId: `level-${String(levelNumber).padStart(3, '0')}`,
    title: `レベル ${levelNumber}`,
  };
}

export function definitionsHash(): string {
  const maskRows = [...rectangleMasks.keys(), ...silhouetteMasks.keys()]
    .sort((left, right) => left.localeCompare(right))
    .map((id) => {
      const mask = maskById(id);
      return [id, mask.width, mask.height, mask.cells.map(({ x, y }) => [x, y])];
    });
  return fnv1a32Hex(JSON.stringify({
    masks: maskRows,
    params: paramsTypes.map(({ id, params }) => [
      id,
      params.lengthMix,
      params.turnBias,
      params.orientationPasses,
      params.warden ?? null,
      params.chainTrap ?? null,
      params.edge ?? null,
      params.vacancy ?? null,
    ]),
    paramsAssignment: {
      seedSalt: paramsTypeSeedSalt,
      candidates: Object.entries(paramsIdsByIntensity),
    },
    intensityRhythm: {
      runLengthWeights: rhythmRunLengthWeights.map(({ length, weight }) => [length, weight]),
      superhardRatio: rhythmSuperhardRatio,
      minimumPeaksBetweenSuperhard: rhythmMinimumPeaksBetweenSuperhard,
      seedSalts: rhythmSeedSalts,
    },
    intensityCellTargets,
    bands: bands.map(({ maxLevel, scale }) => [
      Number.isFinite(maxLevel) ? maxLevel : 'Infinity',
      scale,
    ]),
    rectangleSizeOrder: rectangleMaskIds,
    silhouetteAssignment: {
      algorithmVersion: silhouetteMaskAssignmentAlgorithmVersion,
      introductionMaximumLevel: silhouetteIntroductionMaximumLevel,
      targetRate: silhouetteTargetRate,
      seedSalts: {
        rhythm: silhouetteRhythmSeedSalt,
        maskShuffle: silhouetteMaskShuffleSeedSalts,
      },
      candidates: Object.entries(silhouetteMaskIdsByIntensity),
      suppressConsecutiveLevels: true,
      exhaustMaskPoolBeforeReuse: true,
    },
    reproductionBudgets: {
      largeBoardGenerationPasses,
      largeRectanglePartitionAlgorithmVersion,
      largeRectangleHamiltonianPartitionThreshold,
      largeRectangleShortPathMaximumLength,
      largeRectangleShortPathRatioMinimum,
      largeRectangleShortPathRatioMaximum,
      largeRectangleShortPathMergeThreshold,
      initialRemovableDispersionMinimum,
      wardenOrientationAlgorithmVersion,
      wardenAuthoringRateRanges,
      wardenSuperhardFallbackRateMaximum,
      wardenHardFallbackRateMaximum,
      wardenStrictTargetAttemptCount,
      wardenHardLongBlockerMinimum,
      wardenSuperhardLongBlockerMinimum,
      wardenSuperhardRelaxedLongBlockerMinimum,
      wardenSuperhardPeelEnforcementCellThreshold,
      edgeSentinelAxisArrowCountMinimum,
      edgeSentinelStrictInwardCountPerSideMinimum,
      edgeSentinelRelaxedInwardCountMinimum,
      edgeSentinelStrictAttemptCount,
      vacancyReblockWeight,
      vacancyFreedPenalty,
      vacancyLengthPenalty,
      partitionRestartLimit,
      snakeAttemptLimit,
      generationEffortLimit,
      authoredGenerationRetryLimit,
      smallPeakOrientationAlgorithmVersion,
      chainTrapOrientationAlgorithmVersion,
      vacancyCarvingAlgorithmVersion,
      chainPatchPartitionAlgorithmVersion,
      chainPatchArrowCount,
      chainPatchArrowLength,
      chainPatchCornerMargin,
      chainTrapNormalEnforcementCellThreshold,
      chainTrapNormalStrictAttemptCount,
      chainTrapHardStrictAttemptCount,
      chainTrapHardFallbackTrapMinimum,
      nearSpanningBandGuardAlgorithmVersion,
      nearSpanningBandMaximumEdgeGap,
      nearSpanningBandMinimumConsecutiveLines,
    },
  }));
}
